import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useQuery } from 'react-query';
import Loading from 'components/commons/loading/Loading';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { useRouting } from 'hooks/useRouting';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { getTargetDetail, updateTarget } from 'config/api';
import { formatDate } from 'utils/formatter';
import {
  Target,
  TargetModalStatus,
  TargetType,
  GlobalTargetProps,
} from '../utils/type';
import { targetTypeToTitle } from '../utils/targetInfo';
import { TargetValidateError, validateTarget } from '../utils/validate';
import { useBirthday } from '../utils/birthday';
import { InputRow } from '../components/commons/InputRow';
import { SetPropertiesBase } from '../components/features/share/SetPropertiesBase';
import { useTargetAgeOrYearForm } from '../hooks/useTargetAgeOrYear';
import { useTargetAmountForm } from '../hooks/useTargetAmountForm';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';

export const TargetSetAmountInternal = ({
  id,
  initialTarget,
  birthday,
}: {
  id: string;
  initialTarget: Target;
  birthday: Date;
}) => {
  // Google tag manager
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('chrome') !== -1) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'target_detail',
        targetinfo: `${initialTarget.id}_${
          initialTarget.title
        }_${targetTypeToTitle(initialTarget.refType)}_${formatDate(
          new Date(),
          'yyyyMMddHHmmss'
        )}`,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [errors_, setErrors] = useState<
    | { tag: 'errorDisabled' }
    | { tag: 'errorEnabled'; errors: TargetValidateError[] }
  >({ tag: 'errorDisabled' });

  const errors = (() => {
    switch (errors_.tag) {
      case 'errorDisabled':
        return null;
      case 'errorEnabled':
        return errors_.errors;
    }
  })();

  const amountErrors = useMemo(
    () =>
      (errors ?? []).flatMap(e => (e.tag === 'AmountError' ? [e.error] : [])),
    [errors]
  );

  const { TargetAmountForm, targetAmount } = useTargetAmountForm({
    amountErrors,
    initialTargetAmount: initialTarget.targetAmount,
  });

  const dateErrors = useMemo(
    () => (errors ?? []).flatMap(e => (e.tag === 'DateError' ? [e.error] : [])),
    [errors]
  );

  const { Switch, TargetAgeOrYearForm, absYear } = useTargetAgeOrYearForm({
    birthday,
    dateErrors,
    initialValueYear: new Date(initialTarget.endDate).getFullYear(),
  });

  const target = useMemo(
    () =>
      validateTarget({
        targetType: initialTarget.refType,
        targetTitle: initialTarget.title,
        targetAmount,
        assetAmount: 0,
        targetYear: absYear,
        achieved: false,
        birthYear: birthday.getFullYear(),
        thisYear: new Date().getFullYear(),
      }),
    [
      absYear,
      initialTarget.title,
      initialTarget.refType,
      targetAmount,
      birthday,
    ]
  );

  useEffect(() => {
    setErrors(() => {
      return {
        tag: 'errorEnabled',
        errors: target.tag === 'Error' ? target.errors : [],
      };
    });
  }, [target]);

  const onFinish = useCallback(async (): Promise<void> => {
    switch (target.tag) {
      case 'Success':
        const accountIDList = initialTarget.accountList.map(
          list => list.account
        );
        const startDate = new Date(initialTarget.startDate);
        const endDate = new Date(target.value.endDate);
        const updTargetData = {
          ...initialTarget,
          ...target.value,
          accountIDList,
          startDate,
          endDate,
        };
        await updateTargetAPI(id, updTargetData);
        back();
        break;
      case 'Error':
        setErrors({ tag: 'errorEnabled', errors: target.errors });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTarget, target]);

  const routing = useRouting();
  const back = (): void => {
    routing.pushRouting({ path: `/target` }, true);
  };

  return (
    <>
      <SetPropertiesBase
        {...{
          finishButton: {
            message: <>決定</>,
            isDisabled: target.tag === 'Error',
          },
          onCancel: back,
          onFinish,
        }}>
        <InputRow {...{ title: '目標金額' }} className="mt-6">
          {TargetAmountForm}
        </InputRow>
        <InputRow
          {...{
            title: '達成時期',
            SubInformation: Switch,
          }}
          className="mt-4">
          {TargetAgeOrYearForm}
        </InputRow>
      </SetPropertiesBase>
    </>
  );
};

const TargetSetAmount = (): React.ReactElement => {
  useCustomHeaderTitle('目標金額・時期の見直し');
  const { id } = useParams<{ id: string }>();

  const { setIsShowMenu } = useGlobalMenuContext();
  useEffect(() => {
    return setIsShowMenu(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { setWithBackIconWord } = useCustomizeHeaderContext();
  useEffect(() => {
    setWithBackIconWord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching } = useQuery(
    'targetSetAmountTargetDetail',
    () => getTargetDetailAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );
  const { birthday } = useBirthday();

  if (data === null || data === undefined || isFetching)
    return (
      <>
        <Loading loading={true} style={{ marginTop: '3rem' }} />
      </>
    );

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <TargetSetAmountInternal {...{ id, initialTarget: data, birthday }} />
    </PremiumCheck>
  );
};
const getTargetDetailAPI = async (id: string) => {
  const res_ = await getTargetDetail(id);
  const res = {
    id: res_.ID,
    title: res_.Title,
    refType: res_.RefType as TargetType,
    startDate: res_.StartDate,
    endDate: res_.EndDate,
    targetAmount: res_.TargetAmount,
    accountList: res_.AccountList.map(list => {
      return {
        account: list.Account,
        brokerName: list.BrokerName,
        available: list.Available,
        totalAssetAmount: list.TotalAssetAmount,
      };
    }),
    excludeAmount: res_.ExcludeAmount,
    reserveAmount: res_.ReserveAmount,
    assetAmount: res_.AssetAmount,
    yield: res_.Yield,
    achieved: res_.Achieved,
    status: res_.Status as TargetModalStatus,
  };

  return res as Target;
};

const updateTargetAPI = async (
  id: string,
  target: GlobalTargetProps
): Promise<void> => {
  const dateToString = (date: Date): string =>
    `${date.getFullYear()}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${date
      .getDate()
      .toString()
      .padStart(2, '0')}`;

  await updateTarget({
    ID: id,
    TargetAmount: target.targetAmount,
    EndDate: dateToString(target.endDate),
    StartDate: dateToString(target.startDate),
    Title: target.title,
    RefType: target.refType,
    ...(target.excludeAmount !== null &&
    target.reserveAmount !== null &&
    target.accountIDList !== null
      ? {
          AccountIDList: target.accountIDList,
          ExcludeAmount: target.excludeAmount,
          ReserveAmount: target.reserveAmount,
        }
      : {}),
    ...(target.managementType
      ? { ManagementType: target.managementType }
      : null),
  });
};

export default TargetSetAmount;
